'use client'

import { ReactNode, useEffect, useMemo } from 'react'

import { ErrorViewSection } from '../../../brunswick/components/pages/ErrorView'
import { imageSrcs, relativePaths } from '../../../constants/figs'
import { useAuthentication } from '../../_providers/AuthenticationProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { useUser } from '../../_providers/UserProvider.client'

/** The page content of the generic error boundary that we display across the site. */
export const ErrorPageContents = ({
  error,
  children,
}: {
  /** The source error that triggered this error boundary. */
  error: Error & { digest?: string }
  /** Additional content that should be displayed below the error content. */
  children?: ReactNode
}) => {
  const { userStatus } = useUser()
  const { isLoggedIn, refreshAuthTokensStatus } = useAuthentication()

  const authRefreshStatus = useMemo(() => {
    const authSessionInvalid = userStatus === 'rejected' && isLoggedIn
    const willAttemptRefresh = authSessionInvalid && refreshAuthTokensStatus === 'idle'
    return willAttemptRefresh || userStatus === 'pending' || refreshAuthTokensStatus === 'pending'
      ? 'pending'
      : refreshAuthTokensStatus
  }, [isLoggedIn, refreshAuthTokensStatus, userStatus])
  const errorType = authRefreshStatus === 'idle' ? 'generic' : 'authRefresh'

  useEffect(() => {
    if (authRefreshStatus === 'resolved') {
      window.location.reload()
    } else if (authRefreshStatus === 'rejected') {
      window.location.href = '/'
    }
  }, [authRefreshStatus])

  useEffect(() => {
    if (errorType !== 'authRefresh') {
      // Log the error to an error reporting service
      console.error(error)
    }
  }, [error, errorType])

  const {
    pages: {
      error: {
        genericErrorTitle,
        genericErrorSubtitle,
        authRefreshingTitle,
        authRefreshingErrorSubtitle,
      },
      notFound: {
        image: { alt },
        links,
      },
    },
  } = useTranslation()

  const orderedLinks = useMemo(() => {
    if (errorType === 'generic') {
      return [
        {
          href: relativePaths.shopWomensBestSellersPath,
          title: links.shopWomen.title,
        },
        {
          href: relativePaths.shopMensBestSellersPath,
          title: links.shopMen.title,
        },
      ]
    }
    return []
  }, [errorType, links.shopWomen.title, links.shopMen.title])

  return (
    <>
      <ErrorViewSection
        heading={errorType === 'generic' ? genericErrorTitle : authRefreshingTitle}
        subheading={errorType === 'generic' ? genericErrorSubtitle : authRefreshingErrorSubtitle}
        imageAlt={alt}
        imageSrc={imageSrcs.notFound}
        links={orderedLinks}
      />
      {children}
    </>
  )
}
